import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Download, Upload, CheckCircle2, Clock, AlertTriangle, Loader2, FileSignature } from "lucide-react";
import CandidateDocs from "./CandidateDocs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (d) => new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });

export default function Accept() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/accept/${token}`);
      setInfo(data);
      setState(data.status);
    } catch {
      setState("invalid");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const download = async () => {
    try {
      const res = await axios.get(`${API}/accept/${token}/letter.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Acceptance-Letter.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the letter. The link may have expired.");
    }
  };

  const upload = async () => {
    if (!file) { toast.error("Please choose your signed acceptance file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/accept/${token}/upload`, fd);
      toast.success("Signed acceptance uploaded — thank you!");
      setState("uploaded");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed. Please try again.");
      if (err?.response?.status === 410) setState("expired");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-nx-navy flex items-center justify-center px-4 py-12" data-testid="accept-page">
      <div className="w-full max-w-xl">
        <img src="/assets/logo-signature-dark.png" alt="NexSecure Technologies" className="h-24 mx-auto mb-6" />
        <div className="border border-white/10 bg-white/[0.03] p-8">
          {state === "loading" && <p className="text-slate-400 text-sm text-center" data-testid="accept-loading">Loading…</p>}

          {state === "invalid" && (
            <div className="text-center" data-testid="accept-invalid">
              <AlertTriangle className="mx-auto text-red-400 mb-3" size={32} />
              <h1 className="text-white text-lg">This link is not valid</h1>
              <p className="text-slate-400 text-sm mt-2">Please check the link in your email, or contact HR at hr@nexsecure.in.</p>
            </div>
          )}

          {state === "expired" && (
            <div className="text-center" data-testid="accept-expired">
              <Clock className="mx-auto text-amber-400 mb-3" size={32} />
              <h1 className="text-white text-lg">This link has expired</h1>
              <p className="text-slate-400 text-sm mt-2">Acceptance links are valid for {info?.flow === "v2" ? "24 hours" : "48 hours"} only. Please contact HR at <span className="text-nx-goldlight">hr@nexsecure.in</span> to receive a new link.</p>
            </div>
          )}

          {state === "docs_expired" && (
            <div className="text-center" data-testid="accept-docs-expired">
              <Clock className="mx-auto text-amber-400 mb-3" size={32} />
              <h1 className="text-white text-lg">Your document upload link has expired</h1>
              <p className="text-slate-400 text-sm mt-2">Document links are valid for 7 days. A reminder with a fresh link is sent automatically — please check your email, or contact HR at <span className="text-nx-goldlight">hr@nexsecure.in</span>.</p>
            </div>
          )}

          {state === "cancelled" && (
            <div className="text-center" data-testid="accept-cancelled">
              <AlertTriangle className="mx-auto text-red-400 mb-3" size={32} />
              <h1 className="text-white text-lg">This appointment has been cancelled</h1>
              <p className="text-slate-400 text-sm mt-2">Please refer to the email we sent you. For any questions, write to <span className="text-nx-goldlight">hr@nexsecure.in</span>.</p>
            </div>
          )}

          {state === "confirmed" && info && (
            <div className="text-center" data-testid="accept-confirmed">
              <FileSignature className="mx-auto text-emerald-400 mb-3" size={32} />
              <h1 className="text-white text-lg">Appointment Confirmed</h1>
              <p className="text-slate-300 text-sm mt-2">Thank you, <span className="text-white">{info.employee_name}</span>. Your signed acceptance has been received and your appointment as <span className="text-nx-goldlight">{info.designation}</span> is confirmed. HR will contact you about your joining on {fmtDate(info.joining_date)}.</p>
            </div>
          )}

          {state === "docs" && info && (
            <div data-testid="accept-docs-stage">
              <p className="text-[10px] tracking-[0.28em] uppercase text-nx-goldlight mb-2">Step 1 of 3 — Document verification</p>
              <h1 className="text-white text-lg">Congratulations, {info.employee_name}!</h1>
              <p className="text-slate-300 text-sm mt-2">You have been shortlisted as <span className="text-nx-goldlight">{info.designation}</span> ({info.department}). Please upload all required documents below. Your appointment letter will be issued after the CEO verifies and approves them.</p>
              {info.review?.status === "on_hold" && (
                <div className="border border-amber-500/40 bg-amber-500/5 px-4 py-3 mt-4 text-xs text-amber-300" data-testid="accept-on-hold">Your documents are on hold: {info.review.reason}. Please use the upload link sent to your email.</div>
              )}
              {info.stage === "docs_submitted" || info.review?.status === "pending_review" ? (
                <div className="border border-sky-500/40 bg-sky-500/5 px-4 py-3 mt-4 text-xs text-sky-300" data-testid="accept-docs-submitted">Your documents have been submitted and are awaiting CEO verification. You will receive your appointment letter by email once approved.</div>
              ) : info.docs_expires_at && (
                <div className="border border-amber-500/40 bg-amber-500/5 px-4 py-3 mt-4 text-xs text-amber-300 flex items-start gap-2" data-testid="accept-docs-deadline">
                  <Clock size={14} className="mt-0.5 shrink-0" /><span>This upload link is valid until <b>{fmtDate(info.docs_expires_at)}</b> (7 days).</span>
                </div>
              )}
              <div className="mt-6"><CandidateDocs token={token} /></div>
            </div>
          )}

          {state === "uploaded" && info?.flow !== "v2" && (
            <CandidateDocs token={token} />
          )}
          {state === "uploaded" && info?.flow === "v2" && (
            <div className="text-center" data-testid="accept-confirmed">
              <FileSignature className="mx-auto text-emerald-400 mb-3" size={32} />
              <h1 className="text-white text-lg">Appointment Confirmed</h1>
              <p className="text-slate-300 text-sm mt-2">Thank you — your signed acceptance has been received. HR will be in touch about your joining.</p>
            </div>
          )}

          {state === "pending" && info && (
            <div data-testid="accept-pending">
              <div className="flex items-center gap-2 mb-4">
                <FileSignature size={18} className="text-nx-goldlight" />
                <h1 className="text-white text-lg">Employee Acceptance &amp; Undertaking</h1>
              </div>
              <p className="text-slate-300 text-sm">
                Dear <span className="text-white font-medium">{info.employee_name}</span>, congratulations on your appointment as <span className="text-nx-goldlight">{info.designation}</span> ({info.department}) at NexSecure Technologies.{info.flow === "v2" && " Your documents have been verified and approved by the CEO."}
              </p>
              <div className="border border-amber-500/40 bg-amber-500/5 px-4 py-3 mt-4 text-xs text-amber-300 flex items-start gap-2" data-testid="accept-deadline">
                <Clock size={14} className="mt-0.5 shrink-0" />
                <span>This link is valid until <b>{fmtDate(info.expires_at)}</b> ({info.flow === "v2" ? "24 hours" : "48 hours"}). Your {info.flow === "v2" ? "appointment is confirmed only once" : "onboarding cannot proceed until"} your signed acceptance is uploaded.</span>
              </div>
              <ol className="mt-5 space-y-2 text-sm text-slate-300 list-decimal pl-5">
                <li>Download and print your acceptance letter below.</li>
                <li>Read it carefully and sign the Employee Declaration.</li>
                <li>Upload the signed copy (PDF, JPG or PNG) using the button below.</li>
              </ol>
              <button onClick={download} className="mt-6 w-full flex items-center justify-center gap-2 text-xs tracking-[0.16em] uppercase px-6 py-3.5 border border-nx-gold/60 text-nx-goldlight hover:bg-nx-gold/10 transition-colors" data-testid="accept-download-btn">
                <Download size={14} /> Download Acceptance Letter (PDF)
              </button>
              <div className="mt-6 border-t border-white/10 pt-5">
                <p className="text-[10px] tracking-[0.18em] uppercase text-slate-400 mb-2">Upload Signed Copy (PDF/JPG/PNG ≤10MB)</p>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => fileRef.current?.click()} className="flex items-center gap-2 text-xs px-4 py-3 border border-white/15 text-slate-300 hover:text-white hover:border-white/30 transition-colors" data-testid="accept-file-btn">
                    <Upload size={13} /> {file ? file.name.slice(0, 30) : "Choose File"}
                  </button>
                  <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="accept-file-input" />
                  <button onClick={upload} disabled={busy || !file} className="flex items-center gap-2 text-xs tracking-[0.16em] uppercase px-6 py-3 bg-nx-gold text-nx-navy font-semibold hover:brightness-110 transition-all disabled:opacity-50" data-testid="accept-upload-btn">
                    {busy && <Loader2 size={13} className="animate-spin" />} Submit
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        <p className="text-center text-[11px] text-slate-600 mt-4">NexSecure Technologies · Kathrikadavu, Kaloor, Ernakulam · www.nexsecure.in</p>
      </div>
    </div>
  );
}
